import { useAppSelector } from 'appState/store'
import { Input } from 'components/Forms'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { apiPaths } from 'configs/apiConfigs'
import { updateClaimEntityBag } from 'features/claims/claimsApi'
import { useClaim, useClaimEntityVersion } from 'features/claims/claimsHooks'
import { getLongAgentName, isCurrentUserClaimOwner } from 'features/claims/utils'
import { WellKnownActivitiesEnum, WellKnownActorsEnum } from 'features/entities/types'
import { useRevalidateAllSwrCache } from 'hooks/useRevalidateAllSwrCache'
import React, { ChangeEvent, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FaEdit, FaPencilAlt } from 'react-icons/fa'
import { Button, ObModal, Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'
import { sendLogs } from 'utils'
import { formatPhoneNumber, phoneNumberAutoFormat } from 'utils/formatter'
import { notificator } from 'utils/notificator/notificator'
import { ClaimEntityVersionTable } from './ClaimEntityHistoryTable'
import { EDITABLE_PHONE_SLOTS } from './ProviderRecord/Internal/EntityVersions'

interface IProps {
  show: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
  detail: any
}

export const ClaimEditExtractedModal = ({ show, setShow, detail }: IProps) => {
  const { oaiClaimId } = useParams<{ oaiClaimId: string }>()
  const { data: updatedClaim } = useClaim(oaiClaimId)
  const revalidateAllSwrCache = useRevalidateAllSwrCache()
  const { user } = useAppSelector((state) => state.auth)
  const claimOwnerId = updatedClaim?.ownerId
  const isOwner = isCurrentUserClaimOwner(claimOwnerId, user!)
  const actorName = getLongAgentName(user)

  // Only these slots are editable
  const isEditablePhone = EDITABLE_PHONE_SLOTS.includes(detail?.slot)

  const { data, mutate: mutateClaimEntityVersion } = useClaimEntityVersion(
    detail ? apiPaths.claimEntityVersion(updatedClaim.oaiClaimId!, detail.entId) : null,
  )

  // keep raw digits in state; display as (###-###-####)
  const initialDigits = useMemo(() => String(detail?.value ?? '').replace(/\D/g, ''), [detail?.value])

  const [isEditMode, setIsEditMode] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(true)
  const [evEdited, setEvEdited] = useState<string>(initialDigits) // raw digits
  const [isLoading, setIsLoading] = useState(false)
  const [remarks, setRemarks] = useState('edited by ' + actorName)

  const titleText = detail?.label ?? detail?.description ?? detail?.slot ?? 'Value'

  const submitHandler = () => {
    if (!isEditablePhone) return
    const digits = (evEdited || '').replace(/\D/g, '')
    if (digits.length !== 10) return

    setIsLoading(true)
    const updateData: any = {
      activityId: WellKnownActivitiesEnum.valueChanged,
      actorName,
      actorId: WellKnownActorsEnum.rcmAgent,
      remarks,
      entities: {
        [detail.entId]: evEdited,
      },
    }

    updateClaimEntityBag(updatedClaim.oaiClaimId!, updateData)
      .then(() => {
        mutateClaimEntityVersion()
        setIsLoading(false)
        setIsEditMode(false)
        setShow(false)
        revalidateAllSwrCache()
        notificator.notify({ heading: `Claim successfully updated`, type: 'success' })
      })
      .catch((error) => {
        setIsLoading(false)
        const errorMesssage = error?.response?.data?.parameters?.map((e: any) => e.value) ?? ['Update failed']
        notificator.notify({ heading: errorMesssage.join(' '), type: 'error' })
      })
  }

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const masked = phoneNumberAutoFormat(e.target.value) // eg 123-456-7890
    const digits = masked.replace(/\D/g, '')
    setEvEdited(digits)
    setDisableSubmit(digits.length !== 10)
  }

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text') || ''
    const digits = pastedText.replace(/\D/g, '').slice(-10)

    e.preventDefault()
    setEvEdited(digits)
    setDisableSubmit(digits.length !== 10)
  }

  const changeRemarksHandler = (e: React.ChangeEvent<HTMLInputElement>) => setRemarks(e.target.value)

  const manualEdited = () => (
    <Tooltip
      tooltipText="Manual entries are never automatically overwritten. More recent, extracted versions of this value may exist. Review below."
      position={PositionEnum.bottom}
      positionContent={-20}
      positionTooltipArrow={15}
      tooltipStyles={{
        'max-width': '18rem',
        width: '18rem',
        'text-align': 'left',
        transform: 'translateY(53%) translateX(-15%)',
      }}
    >
      <div className="verion">
        <FaPencilAlt /> Manually entered
      </div>
    </Tooltip>
  )

  return (
    <ObModal
      show={show}
      close={() => setShow(false)}
      size="lg"
      title={'Version History'}
      height="auto"
      customSize="large"
      modalFooter={
        <FooterStyle>
          <Button
            variant="secondary"
            radius="rounded"
            size="large"
            width="100%"
            onClick={submitHandler}
            className="take-ownership-btn"
            disabled={!isEditMode || disableSubmit || !isEditablePhone}
          >
            {isLoading ? (
              <>
                <LoadingAnimation /> Updating... {titleText}
              </>
            ) : (
              <>Update {titleText}</>
            )}
          </Button>

          <Button
            variant="primary"
            radius="rounded"
            size="large"
            width="100%"
            onClick={() => {
              setShow(false)
              setIsEditMode(false)
            }}
          >
            Cancel
          </Button>
        </FooterStyle>
      }
    >
      <ModalInnerDiv>
        <div className="wrapper">
          {isEditMode ? (
            <div className="edit-container">
              <div className="version-holder">
                <div className="title">{titleText}</div>
                <div className="value">
                  {isEditablePhone ? <>{formatPhoneNumber(detail?.value)}</> : <>{detail?.value}</>}
                </div>
                <div className="notification">{detail?.isEditedValue ? manualEdited() : null}</div>
              </div>

              <div className="version-editor">
                <div className="title">New Version</div>
                <div className="form">
                  <Input
                    label="Value"
                    type="text"
                    onChange={handlePhoneChange}
                    onPaste={handlePhonePaste}
                    placeholder={titleText}
                    value={formatPhoneNumber(evEdited)}
                    maxLength={12} // 123-456-7890
                  />
                  <Input
                    label="Comment"
                    type="text"
                    onChange={changeRemarksHandler}
                    placeholder="Adding comments gives reviewers context."
                    value={remarks}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="edit-container-noedit">
              <div className="version-holder-noedit">
                <div className="title">{titleText}</div>
                <div className="value">
                  {isEditablePhone ? <>{formatPhoneNumber(detail?.value)}</> : <>{detail?.value}</>}
                </div>
              </div>

              <div className="version-just-edit">
                {!updatedClaim?.archived && (
                  <>
                    {!isOwner ? (
                      <Tooltip
                        tooltipText="Only assigned owner of this claim can make changes"
                        position={PositionEnum.bottom}
                        positionContent={-20}
                        positionTooltipArrow={85}
                        tooltipStyles={{
                          'max-width': '15rem',
                          width: '15rem',
                          'text-align': 'center',
                          transform: 'translateY(18%) translateX(-65%)',
                        }}
                      >
                        <button
                          className="edit"
                          onClick={() => {
                            if (!isEditablePhone) return
                            setIsEditMode(true)
                            setEvEdited(initialDigits)
                            setDisableSubmit(initialDigits.length !== 10)
                          }}
                          disabled={!isOwner || !isEditablePhone}
                        >
                          <FaEdit /> Edit
                        </button>
                      </Tooltip>
                    ) : (
                      <button
                        className="edit"
                        onClick={() => {
                          if (!isEditablePhone) return
                          sendLogs({ message: 'onClaimVersionHistoryEditClicked' })
                          setIsEditMode(true)
                          setEvEdited(initialDigits)
                          setDisableSubmit(initialDigits.length !== 10)
                        }}
                        disabled={!isEditablePhone}
                      >
                        <FaEdit /> Edit
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {data ? (
            <div className="version-table">
              <ClaimEntityVersionTable details={data?.data} type={'Phone'} />
            </div>
          ) : (
            <div className="loader-container">
              <LoadingAnimation />
              <br />
              <span>Fetching History for {titleText}...</span>
            </div>
          )}
        </div>
      </ModalInnerDiv>
    </ObModal>
  )
}

const FooterStyle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: -webkit-fill-available;
`

const ModalInnerDiv = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.milliard};
  font-style: normal;

  .wrapper {
    display: grid;
  }
  .edit-container-noedit {
    border: 1px solid ${({ theme }) => theme.colors.grey.light};
    border-radius: 5px;
    display: grid;
    grid-template-columns: 4.5fr 1fr;
  }
  .edit-container {
    border: 1px solid ${({ theme }) => theme.colors.grey.light};
    border-radius: 5px;
    display: grid;
    grid-template-columns: 1.5fr 3fr;
  }

  .version-just-edit {
    background: #ecedee; /* Not in Theme*/
    display: grid;
    padding: ${({ theme }) => theme.util.pixelToRem(25)};
    justify-content: right;
    .edit {
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      ${({ theme }) => theme.typography.s.normal};
      align-items: end;
      justify-content: right;
      position: relative;
      top: 10px;
      width: ${({ theme }) => theme.util.pixelToRem(70)};
      height: ${({ theme }) => theme.util.pixelToRem(35)};
      background: none;
      line-height: 16px;
      color: ${({ theme }) => theme.colors.info.darker};
    }
    .edit:hover {
      background: ${({ theme }) => theme.colors.info.lightest};
      border-radius: 5px;
    }
    .edit:disabled {
      color: ${({ theme }) => theme.colors.grey.light};
    }
  }

  .version-holder,
  .version-holder-noedit {
    background: #ecedee; /* Not in Theme*/
    display: flex;
    padding: ${({ theme }) => theme.util.pixelToRem(25)};
    flex-direction: column;

    .title {
      ${({ theme }) => theme.typography.m.normal};
      line-height: 21px;
      color: ${({ theme }) => theme.colors.grey.darker};
    }

    .value {
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      ${({ theme }) => theme.typography.s.normal};
      margin-top: ${({ theme }) => theme.util.pixelToRem(8)};
      line-height: 16px;
      color: ${({ theme }) => theme.colors.grey.darker};
    }

    .notification {
      margin-top: ${({ theme }) => theme.util.pixelToRem(8)};
      display: flex;
      gap: ${({ theme }) => theme.util.pixelToRem(6)};
      .warning {
        background: ${({ theme }) => theme.colors.warning.lightest};
        padding: ${({ theme }) => theme.util.pixelToRem(3)} ${({ theme }) => theme.util.pixelToRem(5)};
        color: ${({ theme }) => theme.colors.warning.darker};
        ${({ theme }) => theme.typography.xs.normal};
        border: 1px solid ${({ theme }) => theme.colors.warning.neutral};
        border-radius: 5px;
      }
      .success {
        background: ${({ theme }) => theme.colors.success.lightest};
        color: ${({ theme }) => theme.colors.success.darker};
        border: 1px solid ${({ theme }) => theme.colors.success.neutral};
        ${({ theme }) => theme.typography.xs.normal};
        padding: ${({ theme }) => theme.util.pixelToRem(3)} ${({ theme }) => theme.util.pixelToRem(5)};
        border-radius: 5px;
      }
      .verion {
        ${({ theme }) => theme.typography.xs.normal};
        background: ${({ theme }) => theme.colors.info.lightest};
        padding: ${({ theme }) => theme.util.pixelToRem(5)};
        border: 1px solid ${({ theme }) => theme.colors.info.light};
        border-radius: 5px;
        color: ${({ theme }) => theme.colors.grey.darker};
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }
  }

  .version-editor {
    background: ${({ theme }) => theme.colors.grey.lightest};
    display: flex;
    padding: ${({ theme }) => theme.util.pixelToRem(20)};
    flex-direction: column;
    .title {
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      ${({ theme }) => theme.typography.m.normal};
      line-height: 18px;
      color: ${({ theme }) => theme.colors.info.darker};
    }
    .form {
      display: grid;
      grid-template-columns: 1fr 3fr;
      gap: ${({ theme }) => theme.util.pixelToRem(20)};
      margin-top: ${({ theme }) => theme.util.pixelToRem(15)};
    }
  }

  .version-table {
    margin: ${({ theme }) => theme.util.pixelToRem(20)} 0px;
  }

  .loader-container {
    position: relative;
    top: 50%;
    left: 50%;
    height: ${({ theme }) => theme.util.pixelToRem(100)};
    margin-top: ${({ theme }) => theme.util.pixelToRem(30)};
    transform: translate(-50%, -50%);
    text-align: center;
  }

  .table {
    font-family: ${({ theme }) => theme.fontFamily.milliard};
    color: ${({ theme }) => theme.colors.grey.darker};
  }
  .table thead tr {
    border-bottom: 2px solid ${({ theme }) => theme.colors.grey.light};
  }
  .table thead tr th {
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    ${({ theme }) => theme.typography.xs.normal};
    text-transform: capitalize;
  }
  .table tbody tr:hover {
    background-color: ${({ theme }) => theme.colors.tertiary.lighter};
  }
`
